/**
 * Global keyword-runs list — every run across every project, newest first.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { KeyRound, Play, Search } from 'lucide-react';
import type { KeywordRun, Project } from '@seo/types';
import { Page, PageHeader, PageToolbar } from '@/components/ui/page';
import { DataTable } from '@/components/data-table';
import { EmptyState } from '@/components/ui/empty';
import { ErrorBanner } from '@/components/error-banner';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StatusPill } from '@/components/status-pill';
import { KeywordRunCreateForm } from '@/features/keywords/components/keyword-run-create-form';
import { useKeywordRuns } from '@/features/keywords/use-keyword-runs';
import { useProjects } from '@/features/projects/use-projects';
import { relTime } from '@/lib/format';

function seedLabel(seed: KeywordRun['seed']): string {
  if (seed.type === 'domain') {
    try {
      return new URL(seed.value).hostname.replace(/^www\./, '');
    } catch {
      return seed.value;
    }
  }
  if (seed.type === 'topics') {
    const head = seed.value.slice(0, 3).join(', ');
    return seed.value.length > 3 ? `${head}…` : head;
  }
  return seed.value;
}

export function KeywordsRoute(): JSX.Element {
  const runs = useKeywordRuns();
  const projects = useProjects();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const projectItems = projects.data?.items ?? [];
  const [createProjectId, setCreateProjectId] = useState('');
  const effectiveCreateProject =
    createProjectId || (projectItems.length === 1 ? projectItems[0]!.id : '');

  const projectsById = useMemo(() => {
    const m = new Map<string, Project>();
    for (const p of projects.data?.items ?? []) m.set(p.id, p);
    return m;
  }, [projects.data]);

  const rows = useMemo(() => {
    const items = runs.data?.items ?? [];
    const q = query.trim().toLowerCase();
    const sorted = [...items].sort((a, b) =>
      (b.created_at ?? '').localeCompare(a.created_at ?? ''),
    );
    if (!q) return sorted;
    return sorted.filter((r) => {
      const p = projectsById.get(r.project_id);
      return (
        r.status.toLowerCase().includes(q) ||
        seedLabel(r.seed).toLowerCase().includes(q) ||
        (p?.name.toLowerCase().includes(q) ?? false)
      );
    });
  }, [runs.data, projectsById, query]);

  return (
    <Page>
      <PageHeader
        title="Keyword research"
        description="Give us a starting point and we'll find hundreds of related keywords, group them by topic, and tell you which are worth chasing."
        actions={
          projectItems.length > 0 ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Play className="h-4 w-4 fill-current" /> New keyword run
            </Button>
          ) : undefined
        }
      />

      {/* The list page used to have no create affordance at all — runs were
          only startable from a project tab. This dialog closes that dead
          end: pick a project, seed a run, land on the run's page. */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogTitle>New keyword run</DialogTitle>
          <DialogDescription className="sr-only">
            Choose a project and seed a keyword research run.
          </DialogDescription>
          <div className="space-y-4">
            {projectItems.length > 1 ? (
              <FormField label="Project">
                <Select
                  value={effectiveCreateProject || undefined}
                  onValueChange={setCreateProjectId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projectItems.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            ) : null}
            {effectiveCreateProject ? (
              <KeywordRunCreateForm
                projectId={effectiveCreateProject}
                onCreated={(run) => {
                  setCreateOpen(false);
                  void navigate({
                    to: '/keywords/$keywordRunId',
                    params: { keywordRunId: run.id },
                  });
                }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                Pick a project to seed the run.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <PageToolbar>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by starting keyword, status, or project..."
            className="h-9 pl-8"
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {rows.length} of {runs.data?.items.length ?? 0}
        </span>
      </PageToolbar>

      {runs.error ? <ErrorBanner error={runs.error} /> : null}

      <DataTable<KeywordRun>
        onRowClick={(r) =>
          void navigate({
            to: '/keywords/$keywordRunId',
            params: { keywordRunId: r.id },
          })
        }
        loading={runs.isLoading}
        rows={rows}
        rowKey={(r) => r.id}
        columns={[
          {
            key: 'status',
            header: 'Status',
            cell: (r) => <StatusPill status={r.status} />,
            className: 'w-32',
          },
          {
            key: 'seed',
            header: 'Starting point',
            cell: (r) => (
              <span className="truncate text-sm font-medium">
                {seedLabel(r.seed)}
              </span>
            ),
          },
          {
            key: 'project',
            header: 'Project',
            cell: (r) => {
              const p = projectsById.get(r.project_id);
              return (
                <span className="text-sm text-muted-foreground">
                  {p?.name ?? r.project_id.slice(0, 8)}
                </span>
              );
            },
          },
          {
            key: 'locale',
            header: 'Market',
            cell: (r) => (
              <Badge variant="outline" className="font-mono text-[10px]">
                {r.country_code} / {r.language}
              </Badge>
            ),
            align: 'right',
            className: 'w-32',
          },
          {
            key: 'when',
            header: 'When',
            cell: (r) => (
              <span className="text-xs text-muted-foreground">
                {relTime(r.created_at)}
              </span>
            ),
            align: 'right',
            className: 'w-32',
          },
          {
            key: 'open',
            header: '',
            cell: (r) => (
              <Link
                to="/keywords/$keywordRunId"
                params={{ keywordRunId: r.id }}
                className="text-xs font-medium text-primary hover:underline"
              >
                Open →
              </Link>
            ),
            align: 'right',
            className: 'w-20',
          },
        ]}
        emptyState={
          <EmptyState
            icon={KeyRound}
            title="Find your next keywords"
            description="Give us a seed keyword or your domain and we'll expand it into a full topic map with volume and difficulty."
            action={
              projectItems.length > 0 ? (
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Play className="h-4 w-4 fill-current" /> New keyword run
                </Button>
              ) : (
                <Button asChild size="sm" variant="outline">
                  <Link to="/projects">Create a project first</Link>
                </Button>
              )
            }
          />
        }
      />
    </Page>
  );
}
